import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { careersPath, enterStep } from '@/modules/careers/careers';
import { CareersJobFacts } from '@/modules/careers/components/careersJobFacts/CareersJobFacts';
import { CareersLayout } from '@/modules/careers/components/careersLayout/CareersLayout';
import { JobApplicationPanel } from '@/modules/careers/components/jobApplicationPanel/JobApplicationPanel';
import type { CareersJobApi, CareersPageApi } from '@/modules/careers/types';
import { Button } from '@/modules/ui/components/button/Button';

import { JobDescription } from './JobDescription';

interface CareersJobViewProps {
  page: CareersPageApi;
  job: CareersJobApi;
  companySlug: string;
  /** Rise the page in; set when it replaces the loading skeleton. */
  animateEnter?: boolean;
}

/**
 * One job's page on the Careers Page: what the role is on the left, the Apply
 * card beside it. The internal job description is never available here.
 */
export function CareersJobView({
  page,
  job,
  companySlug,
  animateEnter = false,
}: CareersJobViewProps) {
  const allRolesPath = careersPath(companySlug);

  return (
    <CareersLayout
      animateEnter={animateEnter}
      company={{ name: page.companyName, slug: companySlug }}
      description={`${job.title} at ${page.companyName}. Apply in a few minutes.`}
      title={`${job.title} at ${page.companyName}`}
    >
      {/* Wrapped: the stagger's transition would override the button's own. */}
      <div {...enterStep(0)} className="self-start">
        <Button
          asChild
          className="text-muted-foreground -ml-3 font-medium [&>svg]:transition-transform [&>svg]:duration-150 [&>svg]:ease-out hover:[&>svg]:-translate-x-0.5"
          variant="ghost"
        >
          <Link to={allRolesPath}>
            <ArrowLeft aria-hidden />
            All roles
          </Link>
        </Button>
      </div>
      <div className="grid grid-cols-1 items-start gap-10 pt-6 md:pt-8 lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-20">
        <article className="flex flex-col gap-8">
          <header {...enterStep(1)} className="flex flex-col gap-4">
            <h1 className="text-[32px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance md:text-[44px]">
              {job.title}
            </h1>
            <CareersJobFacts job={job} withContract />
          </header>
          {job.publicJobDescription && (
            <div {...enterStep(3)}>
              <JobDescription text={job.publicJobDescription} />
            </div>
          )}
        </article>
        <section
          {...enterStep(2)}
          aria-label="Apply"
          className="rounded-2xl border bg-white p-6 shadow-[0_12px_32px_rgb(24_24_28/0.05)] md:p-8 lg:sticky lg:top-24"
        >
          <JobApplicationPanel
            allRolesPath={allRolesPath}
            job={job}
            page={page}
          />
        </section>
      </div>
    </CareersLayout>
  );
}
